<template>
    <page :tabs="tabs">
        <grid ref="grid" :columnDefs="columnDefs" :rowData="rowData" :isSearch="false">
            <responsive-dropdown :icon="['fas', 'bell']" :items="filterByStatusOptions" variant="primary">
                {{ filterByStatus.label }}
            </responsive-dropdown>
            <responsive-button :icon="['fas', 'arrows-rotate']" variant="primary" @click="refresh"
                >Refresh</responsive-button
            >

            <responsive-button :icon="['fas', 'xmark']" variant="primary" @click="isDisableAllDevicesShown = true"
                >Disable All Devices</responsive-button
            >

            <modal
                id="mark-as-disabled-modal"
                title="Disable All"
                ok-title="Confirm"
                @hide="isDisableAllDevicesShown = false"
                @cancel="isDisableAllDevicesShown = false"
                @ok="markAllDisabled"
                :visible="isDisableAllDevicesShown"
                >Are you sure you want to disable push notifications on all devices?</modal
            >
        </grid>
    </page>
</template>

<script>
    import { mapGetters, mapMutations } from 'vuex';
    import API from '../../api';
    import { FCM_DEVICE_ENDPOINT, FCM_DEVICE_ENDPOINT_DISABLE_ALL } from '../../endpoints';
    import Grid from '../generic/grid/grid.vue';
    import Modal from '../generic/modal/modal.vue';
    import page from '../generic/page/page.vue';
    import FormInput from '../generic/form-input/form-input.vue';
    import SearchMultiselect from '../generic/search-multiselect/search-multiselect.vue';
    import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
    import MoreFiltersDropdown from '../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
    import { formatResponseError } from '../../utils/api';
    import DevicesCellRenderer from './devices-cell-renderer.vue';
    import StatusCellRenderer from './status-cell-renderer.vue';

    export default {
        components: {
            page,
            Grid,
            Modal,
            FormInput,
            SearchMultiselect,
            ExportToExcelButton,
            MoreFiltersDropdown,
            ResponsiveDropdown,
            ResponsiveButton,
            devicesCellRenderer: DevicesCellRenderer,
            statusCellRenderer: StatusCellRenderer,
        },
        name: 'DevicesPage',
        data() {
            return {
                busy: false,
                tabs: [
                    {
                        title: 'Notifications',
                        route: '/app/notification/',
                        active: this.$route.path === '/app/notification/',
                    },
                    {
                        title: 'Devices',
                        route: '/app/notification/devices/',
                        active: this.$route.path === '/app/notification/devices/',
                    },
                ],
                rowData: [],
                filterByStatus: {
                    label: 'All',
                    value: null,
                },
                isDisableAllDevicesShown: false,
            };
        },
        computed: {
            columnDefs() {
                return [
                    {
                        field: 'active',
                        headerName: '',
                        cellRenderer: 'statusCellRenderer',
                        minWidth: 80,
                        maxWidth: 80,
                    },
                    {
                        field: 'date_created',
                        headerName: 'Date Added',
                        sortable: false,
                        type: 'dateTimeColumn',
                    },
                    {
                        field: 'name',
                        headerName: 'Device Name',
                        sortable: false,
                        minWidth: 200,
                    },
                    {
                        field: 'type',
                        sortable: false,
                        headerName: 'Type',
                    },
                    {
                        field: 'actions',
                        headerName: 'Actions',
                        sortable: false,
                        cellRenderer: 'devicesCellRenderer',
                        cellRendererParams: {
                            refresh: async () => {
                                try {
                                    const { data } = await API.get(`${FCM_DEVICE_ENDPOINT()}`);
                                    const list = data.filter((item) => {
                                        if (this.filterByStatus.value === null) {
                                            return true;
                                        }
                                        return item.active === this.filterByStatus.value;
                                    });
                                    this.rowData = list;
                                } catch (error) {
                                    const { generic } = formatResponseError(error);
                                    this.errorToast({ text: generic || 'Could not fetch devices' });
                                } finally {
                                    this.busy = false;
                                }
                            },
                        },
                        minWidth: 200,
                    },
                ];
            },
            filterByStatusOptions() {
                const all = {
                    label: 'All',
                    value: null,
                };
                const disabled = {
                    label: 'Disabled',
                    value: false,
                };
                const enabled = {
                    label: 'Enabled',
                    value: true,
                };
                return [
                    {
                        label: all.label,
                        active: this.filterByStatus.value === all.value,
                        click: () => {
                            this.filterByStatus = all;
                            this.refresh();
                        },
                        dropDownItemClass: 'filter-by-status-dropdown-item',
                    },
                    {
                        label: disabled.label,
                        active: this.filterByStatus.value === disabled.value,
                        click: () => {
                            this.filterByStatus = disabled;
                            this.refresh();
                        },
                        dropDownItemClass: 'filter-by-status-dropdown-item',
                    },
                    {
                        label: enabled.label,
                        active: this.filterByStatus.value === enabled.value,
                        click: () => {
                            this.filterByStatus = enabled;
                            this.refresh();
                        },
                        dropDownItemClass: 'filter-by-status-dropdown-item',
                    },
                ];
            },
        },
        mounted() {
            this.fetchData();
        },
        methods: {
            refresh() {
                this.fetchData();
            },
            async fetchData() {
                try {
                    const { data } = await API.get(`${FCM_DEVICE_ENDPOINT()}`);
                    const list = data.filter((item) => {
                        if (this.filterByStatus.value === null) {
                            return true;
                        }
                        return item.active === this.filterByStatus.value;
                    });
                    this.rowData = list;
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({ text: generic || 'Could not fetch devices' });
                } finally {
                    this.busy = false;
                }
            },
            async markAllDisabled() {
                try {
                    await API.post(`${FCM_DEVICE_ENDPOINT_DISABLE_ALL()}`);
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({ text: generic || 'Could not disable devices' });
                }
                this.refresh();
            },
        },
    };
</script>

<style lang="scss" scoped>
    .subscribers-list-wrapper {
        top: 40px;
    }
    .subscribers-wrapper {
        min-width: 300px;
    }
</style>
